import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { StringFilter, PortfolioEnterpriseWithRateCount } from '@mews-commander/data-access';
import { LocalizedText, lt, showSuccessAlert, useDialogAction } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useTableState, TableProvider, TableHeader, Table, IconButton, IconName } from '@optimus-web/core';
import { useUnassignProperties } from './queries';
import { UnassignPropertiesDialog } from './PropertiesTab/UnassignPropertiesDialog';

export const RATE_COUNT_COLUMN_WIDTH = 150;
export const ACTIONS_COLUMN_WIDTH = 64;

const propertiesTableColumnHelper = createColumnHelper<PortfolioEnterpriseWithRateCount>();
type PropertiesTableUsedKeys = keyof Pick<PortfolioEnterpriseWithRateCount, 'name' | 'rateCounts'>;
type PropertyFilters = Partial<Record<PropertiesTableUsedKeys, Array<StringFilter> | null>>;

const useColumns = (onDeleteClick: (enterpriseId: string) => void) => [
    propertiesTableColumnHelper.accessor('name', {
        header: () => <LocalizedText textKey={TextKey.Property} />,
    }),
    propertiesTableColumnHelper.accessor('rateCounts', {
        header: () => <LocalizedText textKey={TextKey.RatePlural} />,
        meta: { width: RATE_COUNT_COLUMN_WIDTH },
        cell: (props) => props.getValue() ?? 0,
    }),
    propertiesTableColumnHelper.display({
        id: 'actions',
        meta: { width: ACTIONS_COLUMN_WIDTH, align: 'end' },
        cell: (props) => (
            <IconButton
                icon={IconName.Unlock}
                onClick={() => onDeleteClick(props.row.original.id)}
                size="small"
                variant="ghost"
                type="submit"
            />
        ),
    }),
];

interface PropertiesTableProps {
    assignedEnterpriseIds: string[] | undefined | null;
    centralRateGroupId: string;
    propertiesList?: Array<PortfolioEnterpriseWithRateCount> | null;
}

export const PropertiesTable = ({
    propertiesList,
    assignedEnterpriseIds,
    centralRateGroupId,
}: PropertiesTableProps) => {
    const [enterpriseIdToUnassign, setEnterpriseIdToUnassign] = useState<string | null>(null);
    const { mutateAsync } = useUnassignProperties();
    const [tableState, setTableState] = useTableState<PortfolioEnterpriseWithRateCount, PropertyFilters>({
        pagination: {
            show: false,
        },
    });

    const unassignPropertyAction = useDialogAction(async () => {
        if (!enterpriseIdToUnassign) {
            return null;
        }

        await mutateAsync(
            { centralRateGroupId, enterpriseIds: [enterpriseIdToUnassign] },
            {
                onSuccess: () => {
                    showSuccessAlert({
                        title: lt('PropertyUnassigned'),
                    });
                },
            }
        );
    });

    const handleModalOpen = (enterpriseId: string) => {
        setEnterpriseIdToUnassign(enterpriseId);
        unassignPropertyAction.handlers.handleOpen();
    };

    const columns = useColumns(handleModalOpen);
    const tableData = useMemo(
        () =>
            assignedEnterpriseIds != null
                ? assignedEnterpriseIds.map((id) => {
                      const propertyData = propertiesList?.find((currProperty) => currProperty.id === id);
                      const rateCount =
                          propertyData?.rateCounts.find((rc) => rc.centralRateGroupId === centralRateGroupId)
                              ?.rateCount || 0;

                      return {
                          ...propertyData,
                          rateCounts: rateCount,
                      };
                  })
                : [],
        [assignedEnterpriseIds, propertiesList, centralRateGroupId]
    );

    return assignedEnterpriseIds != null ? (
        <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
            <TableHeader
                title={<LocalizedText textKey={TextKey.PropertiesNavigation} />}
                subTitle={assignedEnterpriseIds.length}
            />
            <Table data={tableData} amountOfRows={assignedEnterpriseIds.length} uniqueRowId="id" fixedFirstColumn />
            <UnassignPropertiesDialog
                unassignPropertyAction={unassignPropertyAction}
                active={unassignPropertyAction.states.isDialogOpened}
                titleTextKey={TextKey.RateGroupUnassignPropertyDialogTitle}
                descriptionTextKey={TextKey.RateGroupUnassignPropertyDialogDescription}
            />
        </TableProvider>
    ) : null;
};
